import React, { useState } from "react";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    Brush,
} from "recharts";
import useApiGetDailyTrend from "../api-hooks/useApiGetDailyTrend";
import Loading from "../../../ui/Loading";

interface Props {
    initialType: "bookings" | "stripe" | "cash";
    initialYear: string;
    initialMonth: string;
    years: string[];
    months: { value: string; label: string }[];
}

const DailyTrendChart: React.FC<Props> = ({
    initialType,
    initialYear,
    initialMonth,
    years,
    months,
}) => {
    const [type, setType] = useState<"bookings" | "stripe" | "cash">(initialType);
    const [year, setYear] = useState(initialYear);
    const [month, setMonth] = useState(initialMonth);

    // Ensure type is correct for API
    const apiType = type === "cash" ? "cash_transactions" : (type === "stripe" ? "stripe_transactions" : type);

    const { data, isLoading, isError } = useApiGetDailyTrend(apiType, year, month);

    const typeOptions = [
        { value: "bookings", label: "Bookings" },
        { value: "stripe", label: "Stripe" },
        { value: "cash", label: "Cash" },
    ];

    return (
        <div className="bg-white p-4 border rounded shadow mb-6">
            <div className="flex flex-wrap items-center gap-4 mb-4">
                <h3 className="text-lg font-bold">Andamento Giornaliero</h3>
                
                <div className="flex items-center gap-2">
                    <label className="text-sm font-medium">Tipo:</label>
                    <select
                        value={type}
                        onChange={(e) => setType(e.target.value as any)}
                        className="border rounded px-2 py-1 text-sm"
                    >
                        {typeOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="flex items-center gap-2">
                    <label className="text-sm font-medium">Anno:</label>
                    <select
                        value={year}
                        onChange={(e) => setYear(e.target.value)}
                        className="border rounded px-2 py-1 text-sm"
                    >
                        {years.map((y) => (
                            <option key={y} value={y}>
                                {y}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="flex items-center gap-2">
                    <label className="text-sm font-medium">Mese:</label>
                    <select
                        value={month}
                        onChange={(e) => setMonth(e.target.value)}
                        className="border rounded px-2 py-1 text-sm"
                    >
                        {months.map((m) => (
                            <option key={m.value} value={m.value}>
                                {m.label}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="h-[350px] w-full">
                {isLoading ? (
                    <div className="h-full flex items-center justify-center">
                        <Loading />
                    </div>
                ) : isError ? (
                    <div className="h-full flex items-center justify-center text-red-500">
                        Errore nel caricamento dei dati del grafico.
                    </div>
                ) : data && data.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                            {/* @ts-ignore */}
                            <LineChart
                                data={data}
                                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis 
                                    dataKey="date" 
                                    tickFormatter={(tick: any) => {
                                        try {
                                            return tick.split('-')[2]; // Show only day
                                        } catch(e) {
                                            return tick;
                                        }
                                    }}
                                />
                                <YAxis />
                                <Tooltip 
                                    formatter={(value: any, name: any) => {
                                        if (name === "Totale Importo" || name === "Commissioni MyCIA") {
                                            return [`€ ${Number(value).toFixed(2)}`, name];
                                        }
                                        return [value, name];
                                    }}
                                    labelFormatter={(label: any) => `Data: ${label}`}
                                />
                                <Legend />
                                <Line
                                    type="monotone"
                                    dataKey={
                                        type === "bookings" ? "seats" : 
                                        (type === "stripe" || type === "cash" ? "mycia_commission_amount" : "total")
                                    }
                                    name={
                                        type === "bookings" ? "Posti" : 
                                        (type === "stripe" || type === "cash" ? "Commissioni MyCIA" : "Totale Importo")
                                    }
                                    stroke="#f26522" // MyCIA Orange
                                    activeDot={{ r: 8 }}
                                />
                                {(data[0]?.count !== undefined || data[0]?.bookings !== undefined || data[0]?.transactions !== undefined) ? (
                                    <Line 
                                        type="monotone" 
                                        dataKey={
                                            type === "bookings" ? "bookings" : 
                                            (type === "stripe" || type === "cash" ? "transactions" : "count")
                                        } 
                                        name={
                                            type === "bookings" ? "Prenotazioni" : 
                                            (type === "stripe" || type === "cash" ? "Transazioni" : "Conteggio")
                                        } 
                                        stroke="#8884d8"
                                    />
                                ) : null}
                                <Brush 
                                    dataKey="date" 
                                    height={20}
                                    stroke="#f26522" 
                                    fill="#f9f9f9"
                                    travellerWidth={10}
                                    tickFormatter={(tick: any) => {
                                        try {
                                            return tick.split('-')[2];
                                        } catch(e) {
                                            return tick;
                                        }
                                    }}
                                />
                            </LineChart>
                    </ResponsiveContainer>
                ) : (
                    <div className="h-full flex items-center justify-center text-gray-500">
                        Nessun dato disponibile per il periodo selezionato.
                    </div>
                )}
            </div>
        </div>
    );
};

export default DailyTrendChart;
